// @givmo/app-admin — onboarding wizard constants + the "company has no budget" predicate.
//
// `GIVMO_MATCH_CRITERIA` is the verbatim required-language paragraph the Flutter webapp shows
// read-only at the bottom of step 3 (givmo-webapp `Consts.givmoMatchCriteria`). Reproduced
// byte-for-byte (including the curly apostrophes) — Givmo appends it below any custom message.

import type { CompanyDetailedOut } from '@givmo/api-client';

/** Total number of wizard steps (Flutter `kOnboardingStepCount`). */
export const ONBOARDING_STEP_COUNT = 3;

/**
 * The Givmo-required match-criteria language, shown read-only in step 3. VERBATIM copy of the
 * Flutter `Consts.givmoMatchCriteria`; the curly apostrophes (’) are intentional.
 */
export const GIVMO_MATCH_CRITERIA =
  'Our criteria for match-eligible charities is non-binding and subject to change. While this eligibility criteria will guide our decision, we maintain full discretion over whether or not to approve each individual match request for any reason. Charities must first be deemed eligible by Givmo. Please refer to Givmo’s Terms of Use for more information by visiting givmocharitable.org/terms.';

/** Client guard shown when Finish/submit is attempted with no per-employee budget (Flutter parity). */
export const ONBOARDING_BUDGET_REQUIRED_MESSAGE =
  'Please enter a per-employee match budget greater than zero before finishing.';

/**
 * Read the per-employee budget off a company's match policy, tolerating the api-client's
 * `MatchPolicyOut | {} | null` union (the open-record fallback from the generated type). Returns
 * the integer dollars when present, else `null`.
 */
export function readYearBudgetPerEmployee(company: CompanyDetailedOut): number | null {
  const policy = company.match_policy;
  if (policy === null || policy === undefined) return null;
  const value = (policy as { year_budget_per_employee?: unknown }).year_budget_per_employee;
  return typeof value === 'number' ? value : null;
}

/**
 * The onboarding trigger predicate: the company has NO match budget configured. The wizard fires
 * only for such a company (and only when the dismissal flag is unset). A company that already has
 * a budget must reach the app normally — this returns `false` for it.
 */
export function companyNeedsOnboarding(company: CompanyDetailedOut): boolean {
  const budget = readYearBudgetPerEmployee(company);
  return budget === null || budget <= 0;
}
